import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { useCustomers } from "@/hooks/use-customers";
import { useLocation } from "wouter";
type Customer = any;
type InsertCustomer = any;
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Layout } from "@/components/layout";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

import {
  Users,
  UserPlus,
  MoreHorizontal,
  Search,
  X,
  Pencil,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Filter,
  Loader2,
  AlertCircle,
} from "lucide-react";

const CustomerSchema = z.object({
  name: z.string().min(1, "Customer name is required"),
  email: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  notes: z.string().optional().or(z.literal("")),
  taxId: z.string().optional().or(z.literal("")),
  contactPerson: z.string().optional().or(z.literal("")),
  address: z.object({
    street: z.string().optional().or(z.literal("")),
    city: z.string().optional().or(z.literal("")),
    state: z.string().optional().or(z.literal("")),
    zipCode: z.string().optional().or(z.literal("")),
    country: z.string().default("USA"),
  }),
  status: z.enum(["active", "inactive"]).default("active"),
});

type CustomerFormValues = z.infer<typeof CustomerSchema>;

const defaultValues: CustomerFormValues = {
  name: "",
  email: "",
  phone: "",
  notes: "",
  taxId: "",
  contactPerson: "",
  address: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "USA",
  },
  status: "active",
};

// Customer detail view component
const CustomerDetail = ({ customer, onEdit }: { customer: Customer; onEdit: () => void }) => {
  const [_, navigate] = useLocation();
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">{customer.name}</h2>
          {customer.clientId && (
            <p className="text-sm font-mono">
              Client ID: {`${customer.clientId.substring(0, 6)}-${customer.clientId.substring(6)}`}
            </p>
          )}
          <p className="text-muted-foreground">{customer.contactPerson || "No contact person specified"}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant={customer.status === "active" ? "default" : "destructive"}>
            {customer.status === "active" ? "Active" : "Inactive"}
          </Badge>
          <Button variant="outline" size="icon" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-medium mb-2">Contact Information</h3>
          <div className="space-y-2">
            {customer.email && (
              <div className="flex items-center text-sm">
                <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center text-sm">
                <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>{customer.phone}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-2">Address</h3>
          {customer.address ? (
            <div className="flex items-start text-sm">
              <MapPin className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
              <div>
                {customer.address.street && <div>{customer.address.street}</div>}
                {(customer.address.city || customer.address.state || customer.address.zipCode) && (
                  <div>
                    {customer.address.city}{customer.address.city && customer.address.state ? ", " : ""}
                    {customer.address.state} {customer.address.zipCode}
                  </div>
                )}
                {customer.address.country && <div>{customer.address.country}</div>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No address provided</p>
          )}
        </div>
      </div>

      {customer.taxId && (
        <div>
          <h3 className="text-lg font-medium mb-2">Tax ID</h3>
          <p className="text-sm">{customer.taxId}</p>
        </div>
      )}

      {customer.notes && (
        <div>
          <h3 className="text-lg font-medium mb-2">Notes</h3>
          <p className="text-sm whitespace-pre-line">{customer.notes}</p>
        </div>
      )}
    </div>
  );
};

// Customer form component (used for both add and edit)
const CustomerForm = ({ 
  customer, 
  onSubmit, 
  onCancel, 
  isLoading 
}: { 
  customer?: Customer; 
  onSubmit: (data: CustomerFormValues) => void; 
  onCancel: () => void;
  isLoading: boolean;
}) => {
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(CustomerSchema),
    defaultValues: customer ? {
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      notes: customer.notes || "",
      taxId: customer.taxId || customer.tax_id || "",
      contactPerson: customer.contactPerson || "",
      address: {
        street: customer.address?.street || customer.street || "",
        city: customer.address?.city || customer.city || "",
        state: customer.address?.state || customer.state || "",
        zipCode: customer.address?.zipCode || customer.zip_code || customer.zipCode || "",
        country: customer.address?.country || customer.country || "USA",
      },
      status: (customer.status as "active" | "inactive") || "active",
    } : defaultValues,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input placeholder="Customer or company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email address" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input placeholder="Phone number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="contactPerson"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Primary contact name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-medium">Address</h3>

          <FormField
            control={form.control}
            name="address.street"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street</FormLabel>
                <FormControl>
                  <Input placeholder="Street address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="address.city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input placeholder="City" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="State/Province" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="address.zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP Code</FormLabel>
                  <FormControl>
                    <Input placeholder="ZIP/Postal code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input placeholder="Country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="taxId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tax ID</FormLabel>
              <FormControl>
                <Input placeholder="Tax identification number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Additional notes about this customer"
                  {...field} 
                  className="h-24"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>Save</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

// Main Customers Page Component
export default function CustomersPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBusiness } = useCurrentBusiness();
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [isViewCustomerOpen, setIsViewCustomerOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [_, navigate] = useLocation();

  // Get customers data
  const { 
    customers, 
    isLoading, 
    isError, 
    error,
    refetch
  } = useCustomers();
  
  // Debug logging to see customers data
  useEffect(() => {
    console.log("Customers from hook:", customers);
  }, [customers]);

  const {
    createCustomerMutation,
    updateCustomerMutation,
    deleteCustomerMutation,
  } = useCustomers();

  // Filter customers based on search term and status
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = 
      !searchTerm || 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.phone && customer.phone.includes(searchTerm));
    
    const matchesStatus = statusFilter === "all" || customer.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Handle adding a new customer
  const handleAddCustomer = (data: CustomerFormValues) => {
    if (!currentBusiness) {
      toast({
        title: "No active business",
        description: "Please select a business first",
        variant: "destructive",
      });
      return;
    }
    
    // Check for existing email to prevent duplicates
    if (data.email && data.email.trim() !== '') {
      const existingCustomer = customers.find(
        customer => customer.email === data.email
      );
      
      if (existingCustomer) {
        toast({
          title: "Email already exists",
          description: "A customer with this email already exists. Please use a different email.",
          variant: "destructive",
        });
        return;
      }
    }

    createCustomerMutation.mutate({
      ...data,
      businessId: currentBusiness.id,
    }, {
      onSuccess: () => {
        toast({
          title: "Customer added",
          description: "New customer has been added successfully",
        });
        setIsAddCustomerOpen(false);
      },
      onError: (error) => {
        toast({
          title: "Failed to add customer",
          description: error instanceof Error ? error.message : "An unknown error occurred",
          variant: "destructive",
        });
      },
    });
  };

  // Handle updating a customer
  const handleUpdateCustomer = (data: CustomerFormValues) => {
    if (!selectedCustomer) return;
    
    // Check for existing email to prevent duplicates (only if email has changed)
    if (data.email && 
        data.email.trim() !== '' && 
        data.email !== selectedCustomer.email) {
      const existingCustomer = customers.find(
        customer => customer.email === data.email && customer.id !== selectedCustomer.id
      );
      
      if (existingCustomer) {
        toast({
          title: "Email already exists",
          description: "A customer with this email already exists. Please use a different email.",
          variant: "destructive",
        });
        return;
      }
    }

    updateCustomerMutation.mutate({
      id: selectedCustomer.id,
      data,
    }, {
      onSuccess: () => {
        toast({
          title: "Customer updated",
          description: "Customer information has been updated successfully",
        });
        setIsEditCustomerOpen(false);
        setIsViewCustomerOpen(false);
      },
      onError: (error) => {
        toast({
          title: "Failed to update customer",
          description: error instanceof Error ? error.message : "An unknown error occurred",
          variant: "destructive",
        });
      },
    });
  };

  // Handle deleting a customer
  const handleDeleteCustomer = (id: number) => {
    deleteCustomerMutation.mutate(id, {
      onSuccess: () => {
        toast({
          title: "Customer deleted",
          description: "Customer has been deleted successfully",
        });
        setIsDeleteAlertOpen(false);
        setSelectedCustomer(null);
      },
      onError: (error) => {
        toast({
          title: "Failed to delete customer",
          description: error instanceof Error ? error.message : "An unknown error occurred",
          variant: "destructive",
        });
      },
    });
  };

  // Handle view customer — deferred so DropdownMenu closes before Dialog opens
  const handleViewCustomer = (customer: Customer) => {
    requestAnimationFrame(() => {
      setSelectedCustomer(customer);
      setIsViewCustomerOpen(true);
    });
  };

  // Handle edit customer — deferred to avoid Radix focus trap freeze
  const handleEditCustomer = (customer: Customer) => {
    requestAnimationFrame(() => {
      setSelectedCustomer(customer);
      setIsEditCustomerOpen(true);
    });
  };

  // List view
  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <Users className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Customers</h1>
          </div>
          <Button onClick={() => setIsAddCustomerOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchTerm("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-muted/40 rounded-md p-4 mb-6">
            <h3 className="font-medium mb-3">Filters</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="status-filter" className="mb-2 block">
                  Status
                </Label>
                <Select
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                >
                  <SelectTrigger id="status-filter">
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="rounded-md bg-destructive/15 p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5 mr-3" />
              <div>
                <h3 className="font-medium text-destructive">Error loading customers</h3>
                <p className="text-sm text-destructive/80 mt-1">
                  {error instanceof Error ? error.message : "An unknown error occurred"}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-3" 
                  size="sm"
                  onClick={() => refetch()}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        ) : filteredCustomers.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Client ID</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">
                      <Button 
                        variant="ghost" 
                        className="p-0 h-auto font-medium text-left hover:bg-transparent hover:underline"
                        onClick={() => handleViewCustomer(customer)}
                      >
                        {customer.name}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {customer.clientId ? (
                        <span className="font-mono text-sm">
                          {`${customer.clientId.substring(0, 6)}-${customer.clientId.substring(6)}`}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">No ID</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col text-sm">
                        {customer.email && <span>{customer.email}</span>}
                        {customer.phone && <span>{customer.phone}</span>}
                      </div>
                    </TableCell>
                    <TableCell>{customer.contactPerson || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={customer.status === "active" ? "default" : "destructive"}>
                        {customer.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleViewCustomer(customer)}>
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditCustomer(customer)}>
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setIsDeleteAlertOpen(true);
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-12">
            <Users className="h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No customers found</h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchTerm ? "No customers match your search" : "Get started by adding your first customer"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsAddCustomerOpen(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Customer
              </Button>
            )}
          </div>
        )}

        {/* Add Customer Dialog */}
        <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Customer</DialogTitle>
              <DialogDescription>
                Add a new customer to your business
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <CustomerForm
                onSubmit={handleAddCustomer}
                onCancel={() => setIsAddCustomerOpen(false)}
                isLoading={createCustomerMutation.isPending}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Customer Dialog */}
        {selectedCustomer && (
          <Dialog open={isEditCustomerOpen} onOpenChange={setIsEditCustomerOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Customer</DialogTitle>
                <DialogDescription>
                  Update customer information
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4">
                <CustomerForm
                  customer={selectedCustomer}
                  onSubmit={handleUpdateCustomer}
                  onCancel={() => setIsEditCustomerOpen(false)}
                  isLoading={updateCustomerMutation.isPending}
                />
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* View Customer Dialog */}
        {selectedCustomer && (
          <Dialog open={isViewCustomerOpen} onOpenChange={setIsViewCustomerOpen}>
            <DialogContent className="sm:max-w-xl">
              <CustomerDetail 
                customer={selectedCustomer}
                onEdit={() => {
                  setIsViewCustomerOpen(false);
                  setIsEditCustomerOpen(true);
                }}
              />

              <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => navigate(`/sales/customers/${selectedCustomer.id}/invoices/new`)}>
                  Create Invoice
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => navigate(`/sales/customers/${selectedCustomer.id}/estimates/new`)}>
                  Create Estimate
                </Button>
                <Button onClick={() => setIsViewCustomerOpen(false)} className="sm:flex-none">
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Customer Alert Dialog */}
        {selectedCustomer && (
          <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete <strong>{selectedCustomer.name}</strong> and remove all associated data.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteCustomerMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </Layout>
  );
}
